// src/services/readCache.js — last-known-good GET responses.
//
// The outbox keeps a crew's work from being lost. This keeps the app *usable*
// while they're out of signal: every successful read of a field-critical endpoint
// is stashed, and if the same read fails because the network is gone, the stashed
// copy is served instead of an error.
//
// Only reads a crew needs on site are cached — their jobs, the schedule, their
// clock state, notes, work orders, checklists. Billing, payroll, email and admin
// are deliberately excluded: stale money data is worse than no money data, and
// none of it is needed standing in a field.

import axios from 'axios';
import { STORE_READS, dbPut, dbGet, dbGetAll, dbDelete } from './offlineDb';

// Served from cache for this long before it's considered too stale to show at all.
const MAX_AGE_MS = 72 * 60 * 60 * 1000; // 3 days

const CACHEABLE = [
  /^\/auth\/me$/,
  /^\/jobs$/,
  /^\/jobs\/\d+$/,
  /^\/jobs\/map-points$/,
  /^\/jobs\/\d+\/comments$/,
  /^\/jobs\/\d+\/work-orders$/,
  /^\/jobs\/\d+\/photos$/,
  /^\/jobs\/\d+\/phases$/,
  /^\/jobs\/\d+\/field-checklists(\/active)?$/,
  /^\/jobs\/\d+\/parcels$/,
  /^\/jobs\/\d+\/attachments$/,
  /^\/schedule$/,
  /^\/team$/,
  /^\/time-entries\/active$/,
  /^\/time-entries$/,
  /^\/field-checklists\/\d+$/,
];

function normalizePath(config) {
  let url = config.url || '';
  url = url.replace(/^https?:\/\/[^/]+/, '');
  const apiIdx = url.indexOf('/api/');
  if (apiIdx >= 0) url = url.slice(apiIdx + 4);
  return url;
}

function isCacheable(config) {
  if ((config.method || 'get').toLowerCase() !== 'get') return false;
  const path = normalizePath(config).split('?')[0].replace(/\/$/, '') || '/';
  return CACHEABLE.some((re) => re.test(path));
}

// Query string is part of the key — /schedule?start=... is a different view.
function cacheKey(config) {
  const path = normalizePath(config);
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${path}${params}`;
}

export async function readFromCache(key) {
  const row = await dbGet(STORE_READS, key);
  if (!row) return null;
  if (Date.now() - row.cachedAt > MAX_AGE_MS) return null;
  return row;
}

export async function cacheAgeFor(key) {
  const row = await dbGet(STORE_READS, key);
  return row ? Date.now() - row.cachedAt : null;
}

/** Wipe cached reads. Called on logout so the next user can't see them. */
export async function clearReadCache() {
  const rows = await dbGetAll(STORE_READS);
  await Promise.all(rows.map((r) => dbDelete(STORE_READS, r.key)));
}

let installed = false;

export function installReadCache(sharedInstance) {
  if (installed) return;
  installed = true;

  const onSuccess = (response) => {
    try {
      if (response.config && isCacheable(response.config) && response.status === 200) {
        dbPut(STORE_READS, {
          key: cacheKey(response.config),
          data: response.data,
          cachedAt: Date.now(),
        });
      }
    } catch (e) {
      /* caching must never break a working request */
    }
    return response;
  };

  const onError = async (error) => {
    const config = error.config || {};
    // Only substitute when the request never reached the server. A 403 or 500 is
    // a real answer and must surface.
    if (error.response || !isCacheable(config)) return Promise.reject(error);

    const cached = await readFromCache(cacheKey(config));
    if (!cached) return Promise.reject(error);

    return {
      data: cached.data,
      status: 200,
      statusText: 'OK (offline cache)',
      headers: {},
      config,
      // Pages can surface "showing data from 2h ago" off these.
      __fromCache: true,
      __cachedAt: cached.cachedAt,
    };
  };

  if (sharedInstance) sharedInstance.interceptors.response.use(onSuccess, onError);
  axios.interceptors.response.use(onSuccess, onError);
}
